import { mutate } from "swr";
import { Agent, AgentShareToolkitAccess, PartialAgent, Toolkit } from "~/services/agents";
import { apiMutate } from "~/utils/apiClient";

// Project CRUD and attachments live on the Projects service, reached directly
// through the `/api/projects` prefix. Two paths are exceptions: the "start a
// conversation" mutation flows through the analysis-agent (it sets `app` to
// PROJECT_ASSISTANT), and the project skills read is still served by the
// conversations service (the Projects service owns no skills) — see
// `projectSkillsPath` below.
const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API, VITE_PROJECTS_API } = import.meta.env;
export const PROJECTS_API_PREFIX = `${VITE_PROJECTS_API}/projects`;
const CONVERSATIONS_PROJECTS_PREFIX = `${VITE_CONVERSATIONS_API}/projects`;
const ANALYSIS_AGENT_PROJECTS_PREFIX = `${VITE_ANALYSIS_AGENT_API}/projects`;

/** Which slice of projects the list endpoint returns. `usable` is everything the
 *  caller can use; `owned` narrows to those they manage; `all` is the admin
 *  visibility bypass. The UI only offers the views a caller's permissions allow. */
export const ProjectListView = {
  USABLE: "usable",
  OWNED: "owned",
  ALL: "all",
} as const;
export type ProjectListView = (typeof ProjectListView)[keyof typeof ProjectListView];

export const projectListPath = (params?: {
  limit?: number;
  offset?: number;
  view?: ProjectListView;
}) => {
  const query = new URLSearchParams();
  if (params?.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  // The default (`usable`) is omitted so it matches the backend default and
  // shares its cache key with a view-less call.
  if (params?.view && params.view !== ProjectListView.USABLE) {
    query.set("view", params.view);
  }
  const qs = query.toString();
  return qs ? `${PROJECTS_API_PREFIX}?${qs}` : PROJECTS_API_PREFIX;
};

const isProjectListPath = (key: unknown) =>
  typeof key === "string" &&
  (key === PROJECTS_API_PREFIX || key.startsWith(`${PROJECTS_API_PREFIX}?`));

/** Revalidate every cached projects-list key, including the `?view=` variants
 *  (`owned`, `all`). A bare `mutate(projectListPath())` only hits the default
 *  key and would leave a non-default view stale after a create/delete/leave.
 *  Matches the list endpoint and its query variants, as a string key or as the
 *  first element of an array key, not per-project reads. */
export const mutateProjectList = () =>
  mutate((key) => isProjectListPath(Array.isArray(key) ? key[0] : key));

export const projectPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}`;

/** The identifier a project's URL carries: its short_id, falling back to the
 *  uuid while short_id is still nullable. Both resolve on the Projects API. */
export const projectRouteId = (project: { id: string; short_id: string | null }) =>
  project.short_id ?? project.id;

export const projectAgentsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/agents`;
/** Which of an agent's toolkits the project's members couldn't use for lack of
 * scopes. */
export const projectAgentToolkitAccessPath = (projectId: string, agentId: string) =>
  `${projectAgentsPath(projectId)}/${agentId}/toolkit-access`;

export const projectDatasetsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/datasets`;
/** Which of the project's members can't access a dataset. */
export const projectDatasetMemberAccessPath = (projectId: string, datasetId: string) =>
  `${projectDatasetsPath(projectId)}/${datasetId}/member-access`;
/** Whether the caller is missing access to some of the project's agents or
 * datasets. */
export const projectMyResourceAccessPath = (id: string) =>
  `${PROJECTS_API_PREFIX}/${id}/my-resource-access`;

// The skills a conversation in this project would receive: the
// conversations-service resolves the project's attached datasets (dropping any
// the caller can't read), and returns the deduped USER + AGENT + DATASET union
// (no SYSTEM) — the same set the in-conversation picker gets. `agentId` folds
// in that agent's AGENT-scope skills, matching the conversation-scoped path.
// Skills live on the conversations service, not the Projects service, so this
// path keeps the conversations prefix while the rest of the project API is
// served directly by the Projects service.
export const projectSkillsPath = (id: string, agentId?: string | null) =>
  `${CONVERSATIONS_PROJECTS_PREFIX}/${id}/skills${agentId ? `?agent_id=${agentId}` : ""}`;

export const projectConversationsPath = (id: string) =>
  `${PROJECTS_API_PREFIX}/${id}/conversations`;

export const projectLeavePath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/leave`;

export const projectSharesPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/shares`;
/** Which of the project's agents and datasets a prospective share recipient
 * couldn't use. */
export const projectShareResourceAccessPath = (projectId: string, principal: string) =>
  `${projectSharesPath(projectId)}/resource-access?principal=${encodeURIComponent(principal)}`;

export const projectSharesBatchPath = () => `${PROJECTS_API_PREFIX}/shares`;

// `POST /projects/{id}/conversations` still hits the analysis-agent, which
// resolves the project and stamps `app` onto the new conversation row. The
// agent that serves a session is a per-turn selection, not part of create.
const projectConversationsCreatePath = (id: string) =>
  `${ANALYSIS_AGENT_PROJECTS_PREFIX}/${id}/conversations`;

export interface PartialProject {
  id: string;
  // Nullable until the backfill migration is tightened; route by `id` when
  // absent, as the Projects API's own field description instructs.
  short_id: string | null;
  owner_user_id: string;
  name: string;
  description: string | null;
  accent_color: string | null;
  // The Research Workbench provisioned behind this project. Null for projects
  // created before provisioning existed, whose workbench-backed views (Files,
  // Compute Environments, Workflows, Analysis Worksheets) are unavailable.
  workbench_id: number | null;
  created_at: string;
  updated_at: string;
  // The caller's own access to this project, resolved per-request against
  // OpenFGA. Drives gating of owner-only controls in the UI.
  access_level: ProjectAccessLevel;
}

export type Project = PartialProject;

export interface ProjectListItem extends PartialProject {
  agents: PartialAgent[];
  dataset_ids: string[];
  // Greater of the project's creation time and the caller's newest
  // conversation update in it.
  last_activity_at: string;
}

export interface ProjectListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface ProjectList {
  items: ProjectListItem[];
  metadata: ProjectListMetadata;
}

export interface CreateProjectInput {
  name: string;
  description?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
  accent_color?: string | null;
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  return apiMutate<Project>(PROJECTS_API_PREFIX, "POST", input);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "PATCH", input);
}

export async function deleteProject(id: string): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "DELETE");
}

// Remove the caller's own access. The backend returns 204; a 409 means the
// caller is the project's only owner (or access is group-inherited).
export async function leaveProject(id: string): Promise<void> {
  await apiMutate<null>(projectLeavePath(id), "POST", undefined, {
    expects204: true,
  });
}

export async function setProjectAgents(id: string, agent_ids: string[]): Promise<Agent[]> {
  return apiMutate<Agent[]>(projectAgentsPath(id), "PUT", { agent_ids });
}

export async function setProjectDatasets(id: string, dataset_ids: string[]): Promise<string[]> {
  return apiMutate<string[]>(projectDatasetsPath(id), "PUT", { dataset_ids });
}

/** Attach one agent to a project, leaving its other attachments alone.
 * Idempotent, and needs no read of the current set — unlike `setProjectAgents`,
 * whose replace semantics would make this a read-modify-write. */
export async function addProjectAgent(id: string, agentId: string): Promise<Agent[]> {
  return apiMutate<Agent[]>(`${projectAgentsPath(id)}/${agentId}`, "POST");
}

/** Attach one dataset to a project. The dataset counterpart of `addProjectAgent`. */
export async function addProjectDataset(id: string, datasetId: string): Promise<string[]> {
  return apiMutate<string[]>(`${projectDatasetsPath(id)}/${datasetId}`, "POST");
}

export interface ProjectConversation {
  id: string;
  user_id: string;
  // Distinct agents that served this session's turns, first-appearance order.
  // Resolved server-side; empty when unresolved or no turn carries an agent.
  agent_ids: string[];
  // The agent of the session's most recent turn that named one. Resolved
  // server-side from the same query as agent_ids; null when no turn does.
  latest_agent_id: string | null;
  resource_id: string;
  display_name: string;
  status: string;
  status_updated_at: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectConversationList {
  items: ProjectConversation[];
  limit: number | null;
  offset: number;
  total: number;
}

export interface CreateProjectConversationInput {
  user_timezone?: string;
}

export async function createProjectConversation(
  projectId: string,
  input: CreateProjectConversationInput
): Promise<ProjectConversation> {
  return apiMutate<ProjectConversation>(projectConversationsCreatePath(projectId), "POST", input);
}

export const projectFeaturedSessionsPath = (projectId: string) =>
  `${PROJECTS_API_PREFIX}/${projectId}/featured-sessions`;

const projectFeaturedSessionPath = (projectId: string, featuredSessionId: string) =>
  `${PROJECTS_API_PREFIX}/${projectId}/featured-sessions/${featuredSessionId}`;

// A session pinned to a project's featured rail. Owned entirely by the
// Projects service — no relationship to `ProjectConversation` beyond the
// opaque `share_token`, which is how a row links to its read-only view.
// `display_name`/`agent_id` are a snapshot taken when featured (refreshed
// only by re-featuring the same token), not live-synced to the conversation.
// No author field: featuring isn't gated by who created the session, only by
// who manages the project, so the rail shows what a session is, not whose it is.
export interface FeaturedSession {
  id: string;
  project_id: string;
  share_token: string;
  display_name: string;
  created_at: string;
  agent_id: string | null;
}

export interface FeaturedSessionCreate {
  share_token: string;
  display_name: string;
  agent_id?: string | null;
}

/** Pin a session to a project's rail. Idempotent on `share_token`: featuring a
 * token already on the rail refreshes the stored name/agent instead of adding
 * a second row. Requires an existing share — mint one first if needed. */
export async function featureSession(
  projectId: string,
  input: FeaturedSessionCreate
): Promise<FeaturedSession> {
  return apiMutate<FeaturedSession>(projectFeaturedSessionsPath(projectId), "POST", input);
}

/** Unpin a session from a project's rail. Leaves the session's share intact —
 * revoking that is a separate action, and doesn't cascade here either. */
export async function unfeatureSession(
  projectId: string,
  featuredSessionId: string
): Promise<void> {
  await apiMutate<null>(
    projectFeaturedSessionPath(projectId, featuredSessionId),
    "DELETE",
    undefined,
    {
      expects204: true,
    }
  );
}

// Project sharing. Backend maps USER -> can_use and OWNER -> can_manage in
// OpenFGA; principals are users (by email) or user-tags (groups, by name).
export const ProjectAccessLevel = {
  USER: "user",
  OWNER: "owner",
} as const;
export type ProjectAccessLevel = (typeof ProjectAccessLevel)[keyof typeof ProjectAccessLevel];

// A user who inherits access through a shared group (user-tag). Populated only
// on the single-project collaborators endpoint, not the batch list.
/** Project members are its direct users plus its groups' members, each checked
 * once; a project shared with everyone is flagged, not checked member by member. */
// `toolkits` and `members_lacking_access` cover only the members the agent is
// shared with; project access doesn't extend to its agents.
export interface ProjectAgentToolkitAccess extends AgentShareToolkitAccess {
  members_without_agent_access: string[];
  includes_everyone: boolean;
}

/** Project members are its direct users plus its groups' members, each checked
 * once; a project shared with everyone is flagged, not checked member by member. */
export interface ProjectDatasetMemberAccess {
  members_lacking_access: string[];
  member_count: number;
  includes_everyone: boolean;
}

export interface ProjectShareAgentAccess {
  agent_id: string;
  // Toolkits, and the recipients lacking their scopes, among those who can use
  // the agent.
  toolkits: Toolkit[];
  members_lacking_access: string[];
  // Project access doesn't extend to its agents.
  members_without_agent_access: string[];
}

export interface ProjectShareDatasetAccess {
  dataset_id: string;
  members_lacking_access: string[];
}

/** Only resources the sharer can use themselves are checked, and only those
 * some recipient can't use are listed. A group counts each active member. */
export interface ProjectShareResourceAccess {
  agents: ProjectShareAgentAccess[];
  datasets: ProjectShareDatasetAccess[];
  member_count: number;
}

/** Names nothing, since the caller may not be allowed to see what they're
 * missing. Attached resources that no longer exist are ignored. */
export interface ProjectMyResourceAccess {
  missing_dataset_access: boolean;
  missing_agent_access: boolean;
  missing_toolkit_scopes: boolean;
}

export interface ProjectCollaboratorMember {
  email: string;
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
}

export interface ProjectCollaborator {
  /** Bare prefixed principal: `user:{email}` or `user-tag:{tag}`. */
  principal: string;
  level: ProjectAccessLevel;
  // Profile fields, populated for user principals; null for groups.
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
  // Group (user-tag) fields, populated for user-tag principals; absent/empty for users.
  display_name?: string | null;
  is_internal?: boolean;
  members?: ProjectCollaboratorMember[];
}

export interface ProjectCollaboratorList {
  items: ProjectCollaborator[];
}

/** Share a project with a principal at the given level. Idempotent: re-sharing
 * an existing `(principal, level)` succeeds. */
export async function shareProject(
  id: string,
  principal: string,
  level: ProjectAccessLevel
): Promise<ProjectCollaborator> {
  return apiMutate<ProjectCollaborator>(projectSharesPath(id), "POST", {
    principal,
    level,
  });
}

/** Remove a principal from a project. Idempotent: removing an absent principal
 * still succeeds. */
export async function unshareProject(id: string, principal: string): Promise<void> {
  await apiMutate(projectSharesPath(id), "DELETE", { principal }, { expects204: true });
}

// Collaborators for a page of projects, keyed by project id. Fetched in a second
// phase after the list renders so collaborator avatars don't block (or break)
// the list. Projects the caller can't use are omitted from the mapping.
export interface ProjectCollaboratorsBatch {
  collaborators_by_project: Record<string, ProjectCollaborator[]>;
}

export async function fetchProjectCollaboratorsBatch(
  projectIds: string[]
): Promise<ProjectCollaboratorsBatch> {
  return apiMutate<ProjectCollaboratorsBatch>(projectSharesBatchPath(), "POST", {
    project_ids: projectIds,
  });
}
